import { ReactNode } from 'react';
import { BlockInput } from '@/reports/blocks';
import { boundText } from '@/reports/bindings';
import { DataFileRef, FigureBucket, FigureRef, ReadingBucket, ReadingRow, StepBlock, StepBucket, figuresIn, readingsIn, stepsIn } from '@/reports/placement';
import { coverage, fieldValue, printedSteps, resultsMatrix } from '@/reports/standardBlocks';
import { CheckGroup, CheckRow, conditionText, groupChecks, runChecks } from '@/reports/checks';
import { MatrixLine, MatrixTable, procedureParameters } from '@/reports/procedureParameters';
import { ReportEvaluation } from '@/reports/types';
import { Check, DataColumn, DataTable, FigureIndex, GalleryFigure, LimitChart, ReportField, ReportFileLink, ReportGallery, SourceMark, StatusPill, Subsection, SummaryGrid, TableIndex, useSheet, useSheetSection } from '../primitives';
import { dataFileTraceKey } from '@/reports/charts';
import { RunTrace, formatNumber } from '@/reports/runs';

/**
 * How the SDK's blocks draw (their pure halves are src/reports/standardBlocks.ts).
 * Each gets the block's normalized properties and everything the report read,
 * the report's judgement, and a way to fill in a per-run field.
 */

export interface BlockViewProps<P = Record<string, unknown>> {
  input: BlockInput<P>;
  evaluation: ReportEvaluation;
  /** the report is being filled in */
  editing: boolean;
  /** fill in a per-run field the block asks for; absent when the reader can't */
  onField?: (key: string, value: string) => void;
}

const Empty = ({ children = 'Nothing recorded by the run for this section.' }: { children?: ReactNode }) => <p className="small"><span className="unset">{children}</span></p>;

const gallery = (figures: FigureRef[]): GalleryFigure[] => figures.map((f) => ({ key: f.key, fileId: f.fileId, fileName: f.fileName, caption: f.caption }));

const valueCell = (r: ReadingRow): ReactNode => (r.flag === null ? r.value : <StatusPill state={r.flag ? 'pass' : 'fail'}>{r.flag ? 'Yes' : 'No'}</StatusPill>);

/** Where a row was recorded: its step, and how many more steps recorded the same value. */
const recordedAt = (r: ReadingRow): ReactNode => {
  const more = r.alsoAt?.length ?? 0;
  return more ? <>{r.stepTitle} <span className="cell-detail" title={r.alsoAt?.join('\n')}>and {more} more step{more === 1 ? '' : 's'}</span></> : r.stepTitle;
};

const readingColumns = (withStep: boolean): DataColumn<ReadingRow>[] => [
  { key: 'label', title: 'Parameter', render: (r) => r.label },
  { key: 'value', title: 'Recorded value', render: valueCell },
  ...(withStep ? [{ key: 'step', title: 'Recorded at step', render: recordedAt }] : []),
];

const LISTED = 6;

/** The settings a step left as they were: a line naming them, rather than the same table again. */
function UnchangedSettings({ rows }: { rows: ReadingRow[] }) {
  if (rows.length === 0) return null;
  const labels = Array.from(new Set(rows.map((r) => r.label)));
  const listed = labels.slice(0, LISTED).join(', ');
  const rest = labels.length - LISTED;
  return (
    <p className="small unchanged-settings">
      {rows.length === 1 ? '1 setting' : `${rows.length} settings`} as printed earlier in this section: {listed}{rest > 0 ? `, and ${rest} more` : ''}.
    </p>
  );
}

const stepState = (status: string) => (status === 'PASSED' ? 'pass' : status === 'FAILED' ? 'fail' : 'warn');

type Props<P> = BlockViewProps<P>;

export function TextBlockView({ input }: Props<{ text: string; fallback: string }>) {
  const text = input.props.text.trim();
  const description = input.props.fallback === 'procedure-description' ? input.run.description?.trim() ?? '' : '';
  if (text) return <p style={{ whiteSpace: 'pre-line' }}>{text}</p>;
  if (description) return <p style={{ whiteSpace: 'pre-line' }}>{description}</p>;
  return <Empty>Not written yet — set in the report’s layout.</Empty>;
}

export function FieldBlockView({ input, onField }: Props<{ label: string; help: string; multiline: boolean; required: boolean }>) {
  const { block, props } = input;
  return (
    <ReportField
      id={`field-${block.id}`}
      label={props.label || 'Notes'}
      value={fieldValue(input)}
      onChange={onField ? (v) => onField(block.id, v) : undefined}
      multiline={props.multiline}
      optional={!props.required}
      help={props.help || undefined}
    />
  );
}

export function ReadingsBlockView({ input }: Props<{ bucket: string; title: string; showStep: boolean }>) {
  const rows = readingsIn(input.placed, (input.props.bucket || 'parameters') as ReadingBucket);
  if (rows.length === 0) return <Empty />;
  return <DataTable title={input.props.title || undefined} numbered={!!input.props.title} tableKey={input.block.id} columns={readingColumns(input.props.showStep)} rows={rows} rowKey={(r) => r.key} rowState={(r) => (r.flag === false ? 'fail' : undefined)} wrap />;
}

export function GalleryBlockView({ input }: Props<{ bucket: string; title: string }>) {
  const figures = figuresIn(input.placed, (input.props.bucket || 'setup') as FigureBucket);
  if (figures.length === 0) return input.props.title ? null : <Empty />;
  return <ReportGallery title={input.props.title || undefined} figures={gallery(figures)} />;
}

export function DutBlockView({ input }: Props<Record<string, never>>) {
  const { run, placed, bindings } = input;
  const part = run.dut;
  // DUT parameters the report's binding table names, where the procedure names them otherwise
  const shown = new Set(placed.dut.readings.map((r) => r.identifier));
  const bound = Object.values(bindings).filter((b) => b.requirement.role?.startsWith('dut.') && b.source === 'template' && b.requirement.kind !== 'file' && b.matches.length > 0 && !shown.has(b.matches[0].reading.identifier));
  const empty = !part && placed.dut.readings.length === 0 && placed.dut.figures.length === 0 && bound.length === 0;
  return (
    <>
      {part && (
        <SummaryGrid items={[
          { label: 'Device under test', value: part.title },
          ...(part.serial ? [{ label: 'Serial number', value: part.serial }] : []),
          ...(part.description ? [{ label: 'Description', value: part.description }] : []),
        ]} />
      )}
      {(placed.dut.readings.length > 0 || bound.length > 0) && (
        <SummaryGrid items={[
          ...placed.dut.readings.map((r) => ({ label: r.label, value: valueCell(r) })),
          ...bound.map((b) => ({ label: b.requirement.label, value: <>{boundText(b) ?? ''} <SourceMark source="template" note={`bound in the report to ${b.matches[0].reading.identifier}`} /></> })),
        ]} />
      )}
      <ReportGallery figures={gallery(placed.dut.figures)} />
      {empty && <Empty />}
    </>
  );
}

export function EquipmentBlockView({ input }: Props<Record<string, never>>) {
  const equipment = (input.run.equipment ?? []).map((e) => ({
    name: e.name,
    description: e.description,
    details: Object.entries(e.configuration).filter(([, x]) => x !== null && x !== '').map(([k, x]) => `${k}: ${x}`).join(' · '),
  }));
  if (equipment.length === 0) return <Empty>The run lists no equipment.</Empty>;
  return (
    <DataTable
      title="Equipment"
      numbered
      tableKey={input.block.id}
      columns={[
        { key: 'name', title: 'Instrument', render: (r) => r.name },
        { key: 'description', title: 'Description', render: (r) => r.description },
        { key: 'details', title: 'Details', render: (r) => r.details },
      ]}
      rows={equipment}
      rowKey={(r, i) => `${r.name}-${i}`}
      wrap
    />
  );
}

/** A data file drawn as it was recorded, x against y, numbered as a figure with the file as its source. */
function DataFileChart({ file, trace }: { file: DataFileRef; trace: RunTrace }) {
  const sheet = useSheet();
  const section = useSheetSection();
  const entry = sheet?.figure(`chart-${file.fileId}`, { caption: file.caption, fileId: file.fileId, fileName: file.fileName, section: section?.title ?? null });
  const ys = trace.points.flatMap((p) => [p[1], p[2]]);
  const range = ys.length ? `, ${formatNumber(Math.min(...ys))} to ${formatNumber(Math.max(...ys))}` : '';
  return (
    <LimitChart
      id={entry?.anchor}
      points={trace.points}
      seriesLabel={trace.yLabel ?? file.caption}
      xLabel={trace.xLabel ?? 'First column of the file'}
      yLabel={trace.yLabel ?? 'Second column of the file'}
      height={220}
      ariaLabel={`${file.caption}, drawn from ${file.fileName}`}
      caption={<>{entry && <b>Figure {entry.number}.</b>} {file.caption} — {file.fileName}, {trace.samples.toLocaleString('en-US')} samples{range}.</>}
    />
  );
}

/** One step: its recorded values, its checks, its figures, and where its data files went. */
function StepBlockView({ block, traces }: { block: StepBlock; traces?: Record<string, RunTrace> }) {
  const charted = block.files.map((f) => ({ file: f, trace: traces?.[dataFileTraceKey(f.fileId)] })).filter((c): c is { file: DataFileRef; trace: RunTrace } => !!c.trace && !c.trace.error && c.trace.points.length > 0);
  return (
    <Subsection id={`step-${block.id}`} title={block.title}>
      <p className="small">
        {block.context && <span>{block.context} · </span>}
        <StatusPill state={stepState(block.status)}>{block.status || 'NOT RECORDED'}</StatusPill>
      </p>
      {block.readings.length > 0 && <DataTable columns={readingColumns(false)} rows={block.readings} rowKey={(r) => r.key} rowState={(r) => (r.flag === false ? 'fail' : undefined)} wrap />}
      <UnchangedSettings rows={block.unchanged} />
      {block.validations.map((v, i) => (
        <Check key={`${block.id}-v${i}`} finding={{ id: `${block.id}-v${i}`, kind: v.passed ? 'pass' : 'defect', severity: 'fail', passed: v.passed, message: v.message ? `${v.title}: ${v.message}` : v.title }} />
      ))}
      <ReportGallery figures={gallery(block.figures)} />
      {charted.map((c) => <DataFileChart key={c.file.key} file={c.file} trace={c.trace} />)}
      {block.files.length > 0 && <p className="small">Data files recorded here: {block.files.map((f) => f.fileName).join(', ')} — see the appendix.</p>}
    </Subsection>
  );
}

export function StepsBlockView({ input }: Props<{ bucket: string; exceptChecked: boolean }>) {
  const blocks = printedSteps(input);
  const checked = stepsIn(input.placed, (input.props.bucket || 'data') as StepBucket).length - blocks.length;
  // steps the checks table shows in full are said to be there, so the section never reads as if nothing happened in it
  const note = checked > 0 && (
    <p className="small unchanged-settings">
      {checked === 1 ? '1 step' : `${checked} steps`} of this section {checked === 1 ? 'records' : 'record'} only a yes/no result and the values it compared; {checked === 1 ? 'it is' : 'they are'} listed with the checks in the conclusion.
    </p>
  );
  if (blocks.length === 0) return note || <Empty />;
  return <>{blocks.map((b) => <StepBlockView key={b.id} block={b} traces={input.context.traces} />)}{note}</>;
}

export function ResultsMatrixView({ input }: Props<Parameters<typeof resultsMatrix>[0]['props']>) {
  const matrix = resultsMatrix(input);
  const unit = input.props.unit ? ` ${input.props.unit}` : '';
  if (matrix.problem) return <Empty>{matrix.problem}</Empty>;
  const title = input.props.title || 'Results';
  const table = (split: string | null) => {
    const cell = (row: string, col: string) => matrix.cells.find((c) => c.split === split && c.row === row && c.col === col);
    return (
      <DataTable
        key={split ?? ''}
        title={split === null ? title : `${title} — ${split}`}
        numbered
        tableKey={split === null ? input.block.id : `${input.block.id}-${split}`}
        columns={[
          { key: 'row', title: '', render: (r: string) => <b>{r}</b> },
          ...matrix.cols.map((col) => ({
            key: col,
            title: col,
            align: 'right' as const,
            render: (row: string) => {
              const c = cell(row, col);
              if (!c) return <span className="unset">not run</span>;
              return (
                <>
                  {c.value === null ? '—' : `${formatNumber(c.value)}${unit}`}
                  {c.state !== 'none' && <> <StatusPill state={c.state}>{c.state === 'pass' ? 'PASS' : 'FAIL'}</StatusPill></>}
                  {c.limit !== null && <span className="cell-detail">limit {formatNumber(c.limit)}{unit}</span>}
                </>
              );
            },
          })),
        ]}
        rows={matrix.rows}
        rowKey={(r) => r}
      />
    );
  };
  return matrix.splits.length ? <>{matrix.splits.map(table)}</> : table(null);
}

const LISTED_OFFERED = 8;

const offeredText = (offered: string[]) => (offered.length > LISTED_OFFERED ? `${offered.slice(0, LISTED_OFFERED).join(', ')}, and ${offered.length - LISTED_OFFERED} more` : offered.join(', '));

export function CoverageView({ input }: Props<Parameters<typeof coverage>[0]['props']>) {
  const model = coverage(input);
  if (model.problem) return <Empty>{model.problem}</Empty>;
  // what the procedure offers beyond this run's selection, where a dimension is one of its lists
  const offered = [input.props.rows, input.props.cols]
    .map((id) => input.dimensions.find((d) => d.id === id))
    .flatMap((d) => (d?.offered?.length ? [`${d.label.toLowerCase()}: ${offeredText(d.offered.map((o) => o.label))}`] : []));
  return (
    <>
      <DataTable
        title={input.props.title || 'Coverage'}
        numbered
        tableKey={input.block.id}
        columns={[
          { key: 'row', title: '', render: (r: string) => <b>{r}</b> },
          ...model.cols.map((col) => ({
            key: col,
            title: col,
            render: (row: string) => {
              const steps = model.executed[row]?.[col] ?? [];
              return steps.length ? <StatusPill state="pass">RUN</StatusPill> : <StatusPill state="none">NOT RUN</StatusPill>;
            },
          })),
        ]}
        rows={model.rows}
        rowKey={(r) => r}
      />
      {offered.length > 0 && <p className="small unchanged-settings">Also offered by the procedure, not selected for this run — {offered.join('; ')}.</p>}
    </>
  );
}

/** The procedure's test matrix: what it loops over, which of it this run ran, and the lists it carries. */
export function ProcedureParametersView({ input }: Props<Record<string, never>>) {
  const { lines, tables, values } = procedureParameters(input.run);
  return (
    <>
      {values.length > 0 && <SummaryGrid items={values} />}
      {lines.length > 0 && (
        <DataTable
          title="What the procedure runs over"
          numbered
          tableKey={`${input.block.id}-lines`}
          columns={[
            { key: 'title', title: 'Parameter', render: (l: MatrixLine) => l.title },
            { key: 'run', title: 'In this run', render: (l: MatrixLine) => (l.inRun.length ? l.inRun.join(', ') : <span className="unset">none selected</span>) },
            { key: 'offered', title: 'Also offered, not run here', render: (l: MatrixLine) => offeredText(l.offered) },
          ]}
          rows={lines}
          rowKey={(l) => l.key}
          wrap
        />
      )}
      {tables.map((t: MatrixTable) => (
        <div key={t.key}>
          <DataTable
            title={t.title}
            numbered
            tableKey={`${input.block.id}-${t.key}`}
            columns={[{ key: 'label', title: '', render: (r: MatrixTable['rows'][number]) => <b>{r.label}</b> }, ...t.columns.map((c) => ({ key: c.key, title: c.label, align: 'right' as const, render: (r: MatrixTable['rows'][number]) => r.cells[c.key] || '—' }))]}
            rows={t.rows}
            rowKey={(r) => r.key}
            emptyText="None selected for this run"
            wrap
          />
          {t.choice && t.offered.length > 0 && <p className="small unchanged-settings">{t.rows.length ? 'Run here as above; also' : 'None run here;'} offered by the procedure: {offeredText(t.offered)}.</p>}
        </div>
      ))}
    </>
  );
}

const LISTED_CONDITIONS = 3;

/** Where a check was made: its conditions, or its step where the procedure names none. */
function madeAt(group: CheckGroup): string {
  const places = Array.from(new Set(group.rows.map((r) => conditionText(r) || r.step.title)));
  if (places.length <= LISTED_CONDITIONS) return places.join('; ');
  return `${places.length} ${group.rows.some((r) => r.condition.length) ? 'conditions' : 'steps'}`;
}

/** A validation passes or fails; a yes/no is reported as recorded, which is the run's to judge. */
function groupResult(group: CheckGroup): ReactNode {
  if (group.source === 'validation') {
    return group.failed === 0
      ? <StatusPill state="pass">PASS</StatusPill>
      : <><StatusPill state="fail">FAIL</StatusPill> <span className="cell-detail">{group.failed} of {group.rows.length} failed</span></>;
  }
  if (group.failed === 0) return <StatusPill state="pass">{group.rows.length === 1 ? 'Yes' : `Yes, all ${group.rows.length}`}</StatusPill>;
  if (group.passed === 0) return <StatusPill state="warn">{group.rows.length === 1 ? 'No' : `No, all ${group.rows.length}`}</StatusPill>;
  return <StatusPill state="warn">No at {group.failed} of {group.rows.length}</StatusPill>;
}

/** Where a validation failed or a yes/no was recorded No: the first few conditions, and the first failure's message. */
function failedAt(group: CheckGroup): ReactNode {
  const failed = group.rows.filter((r) => !r.passed);
  if (failed.length === 0) return '';
  const places = failed.map((r) => conditionText(r) || r.step.title);
  const listed = places.slice(0, LISTED_CONDITIONS).join('; ');
  const first = failed[0].message ? <span className="cell-detail">{failed[0].message.split('\n')[0]}</span> : null;
  return <>{listed}{places.length > LISTED_CONDITIONS ? `; and ${places.length - LISTED_CONDITIONS} more` : ''}{first}</>;
}

const rowResult = (r: CheckRow) => (r.source === 'validation'
  ? <StatusPill state={r.passed ? 'pass' : 'fail'}>{r.passed ? 'PASS' : 'FAIL'}</StatusPill>
  : <StatusPill state={r.passed ? 'pass' : 'warn'}>{r.passed ? 'Yes' : 'No'}</StatusPill>);

/** One yes/no result at each condition it was recorded at, with the values its step compared. */
function CheckDetail({ group, tableKey }: { group: CheckGroup; tableKey: string }) {
  const value = (r: CheckRow, identifier: string) => r.values.find((x) => x.identifier === identifier)?.value ?? '—';
  return (
    <DataTable
      title={group.name}
      numbered
      tableKey={tableKey}
      columns={[
        { key: 'condition', title: 'Condition', render: (r: CheckRow) => conditionText(r) || r.step.title },
        ...group.columns.map((c) => ({ key: c.identifier, title: c.label, align: 'right' as const, render: (r: CheckRow) => value(r, c.identifier) })),
        { key: 'result', title: 'Result', render: rowResult },
      ]}
      rows={group.rows}
      rowKey={(r) => r.key}
      rowState={(r) => (r.passed ? undefined : 'warn')}
      wrap
    />
  );
}

export function ChecksView({ input }: Props<{ title: string; details: boolean }>) {
  const groups = groupChecks(runChecks(input.run));
  if (groups.length === 0) return <Empty>The run recorded no checks.</Empty>;
  const detailed = input.props.details ? groups.filter((g) => g.source === 'flag' && g.columns.length > 0) : [];
  return (
    <>
      <DataTable
        title={input.props.title || 'Checks recorded by the run'}
        numbered
        tableKey={input.block.id}
        columns={[
          { key: 'check', title: 'Check', render: (g: CheckGroup) => g.name },
          { key: 'where', title: 'Made at', render: madeAt },
          { key: 'result', title: 'Result', render: groupResult },
          { key: 'failed', title: 'Exceptions', render: failedAt },
        ]}
        rows={groups}
        rowKey={(g) => g.key}
        rowState={(g) => (g.failed > 0 && g.source === 'validation' ? 'fail' : undefined)}
        wrap
      />
      {detailed.map((g) => <CheckDetail key={g.key} group={g} tableKey={`${input.block.id}-${g.key}`} />)}
    </>
  );
}

const fileColumns: DataColumn<DataFileRef>[] = [
  { key: 'file', title: 'File', render: (r) => <ReportFileLink fileId={r.fileId}>{r.fileName}</ReportFileLink>, mono: true },
  { key: 'caption', title: 'Recorded as', render: (r) => r.caption },
  { key: 'step', title: 'Recorded at step', render: (r) => r.stepTitle },
];

export function DataFilesView({ input }: Props<Record<string, never>>) {
  if (input.placed.files.length === 0) return <Empty>The run recorded no data files.</Empty>;
  return <DataTable title="Data files" numbered tableKey={input.block.id} columns={fileColumns} rows={input.placed.files} rowKey={(r) => r.key} wrap />;
}

export function FindingsView({ evaluation }: Props<Record<string, never>>) {
  if (evaluation.findings.length === 0) return <Empty>The report makes no checks for this run.</Empty>;
  return <>{evaluation.findings.map((f) => <Check key={f.id} finding={f} />)}</>;
}

export const FigureIndexView = () => <FigureIndex />;
export const TableIndexView = () => <TableIndex />;
